/**
 * The packages duck. Default export is the reducer.
 *
 * Action Types:
 * - PACKAGES_FETCH_REQUESTED
 * - PACKAGES_FETCH_SUCCEEDED
 * - PACKAGES_FETCH_FAILED
 * - FAVORITE_PACKAGES_FETCH_REQUESTED
 *
 * Action Creators:
 * - packagesRequest
 * - packagesFailed
 * - packagesFetched
 * - favoritePackagesRequest
 */
import { getPercentOf } from '../utils/math';

import type {
  PackagesAction,
  PackagesFetch,
  FavoritePackagesFetch,
  PackagesFetchFailed,
  PackagesRequestOpts,
  FavoritePackagesRequestOpts,
  PackagesFetchSucceeded,
  PackagesFetchedOptions,
  PackagesState,
} from '../types';

/** @type {ActionType} */
export const PACKAGES_FETCH_REQUESTED = 'PB/packages/FETCH_REQUESTED';
/** @type {ActionType} */
export const PACKAGES_FETCH_SUCCEEDED = 'PB/packages/FETCH_SUCCEEDED';
/** @type {ActionType} */
export const PACKAGES_FETCH_FAILED = 'PB/packages/FETCH_FAILED';
/** @type {ActionType} */
export const FAVORITE_PACKAGES_FETCH_REQUESTED =
  'PB/packages/FETCH_FAVORITES_REQUESTED';

export const initialState = {
  color: '',
  data: [],
  hasManyInvitations: false,
  loading: false,
  logo: '',
  promoBanner: { logo: {} },
  navNode: {
    id: undefined,
    desktopDefaultCrossSellingTab: '',
    desktopFlyerCategories: [],
    mobileDefaultCrossSellingTab: '',
    mobileFlyerCategories: [],
    promos: [],
    slug: '',
    title: '',
    type: 'section',
    seo: {},
    childNodes: [],
  },
  query: '',
  seo: {
    canonicalUrl: '',
    description: '',
    h1: '',
    footer: '',
    title: '',
  },
  title: '',
  total: 0,
  packageType: undefined,
};

/** @ignore */
export default function packages(
  state: PackagesState = initialState,
  action: PackagesAction,
): PackagesState {
  switch (action.type) {
    case PACKAGES_FETCH_REQUESTED:
    case FAVORITE_PACKAGES_FETCH_REQUESTED:
      return { ...state, loading: true };

    case PACKAGES_FETCH_SUCCEEDED:
      const payload = action.payload || {};

      // we need to assign some defaults to some things
      const {
        color = '',
        packages: data = [],
        logo = '',
        navNode = initialState.navNode,
        group = initialState.navNode,
        section = initialState.navNode,
        query = '',
        seo = initialState.seo,
        title = '',
        total = 0,
        pagination,
        ...rest
      } = payload;

      // track number of invitations (event type is 'rsvp_event')
      let invitationCount = 0;

      // Determine what type of card has a plurality on the page.  This becomes the packageType.
      let maxType;
      data.reduce((accum, ele) => {
        let eventType = ele.eventType;
        let eventCount = accum[eventType];
        accum[eventType] = !eventCount ? 1 : eventCount + 1;
        if (!maxType || accum[eventType] > accum[maxType]) {
          maxType = eventType;
        }
        if (ele.eventType === 'rsvp_event') {
          invitationCount++;
        }
        return accum;
      }, {});

      let packageType = !!maxType ? maxType : initialState.packageType;

      return {
        // pass through the rest of the payload that needs no default
        ...rest,
        color,
        data,

        // if 25% or more are invitations
        hasManyInvitations:
          invitationCount >=
          getPercentOf({
            input: data.length,
            percent: 25,
          }),

        logo,
        navNode,
        group,
        section,
        query,
        seo,
        title,
        total,
        packageType,
        // and set loading to false
        loading: false,
      };

    case PACKAGES_FETCH_FAILED:
      return { ...state, loading: false };

    default:
      return state;
  }
}

/**
 * This action creator will return a PACKAGES_FETCH_REQUESTED action. This type
 * of action is intercepted by a epic and kicks off an API request.
 *
 * @param {Object} opts
 * @param {string} opts.type - Can be a category, group, or section
 * @param {string} opts.slug - The category, group, or section name
 * @param {number} [opts.page] - The page number to fetch
 * @return {Action}
 * @property {string} type PACKAGES_FETCH_REQUESTED
 * @property {Object} payload The payload param is passed through to the action.
 */
export function packagesRequest({
  type,
  slug,
  perPage = 28,
  query = {},
}: PackagesRequestOpts): PackagesFetch {
  // Stripping out values which will override the real values and cause real headaches
  const {
    category,
    group,
    section, // eslint-disable-line no-unused-vars
    ...filteredQuery
  } = query;

  const params =
    type !== undefined && slug !== undefined ? { [type]: slug } : {};

  return {
    type: PACKAGES_FETCH_REQUESTED,
    payload: {
      per_page: perPage,
      ...params,
      ...filteredQuery,
    },
  };
}

/**
 * This action creator will return a FAVORITE_PACKAGES_FETCH_REQUESTED action. This type
 * of action is intercepted by a epic and kicks off an API request.
 *
 * @param {Object} opts
 * @return {Action}
 * @property {string} type FAVORITE_PACKAGES_FETCH_REQUESTED
 * @property {Object} payload The payload param is passed through to the action.
 */
export function favoritePackagesRequest({
  query = {},
}: FavoritePackagesRequestOpts): FavoritePackagesFetch {
  return {
    type: FAVORITE_PACKAGES_FETCH_REQUESTED,
    payload: {
      ...query,
    },
  };
}

/**
 * This action creator will return a PACKAGES_FETCH_FAILED action given an error
 * message.
 *
 * @param {string} message - A message summarizing the error. May be shown to
 *     the user (So watch yr language).
 * @return {Action}
 * @property {string} type PACKAGES_FETCH_FAILED
 * @property {Object} payload The payload is an object containing the message
 *     e.g.`{ message: '' }`
 */
export function packagesFailed(message: string): PackagesFetchFailed {
  return {
    type: PACKAGES_FETCH_FAILED,
    payload: { message },
  };
}

/**
 * This action creator will return a PACKAGES_FETCH_SUCCEEDED action given an
 * array of _parsed_ packages.
 *
 * @param {Packages[]} packages - an array of packages (parsed, please).
 * @return {Action}
 * @property {string} type PACKAGES_FETCH_SUCCEEDED
 * @property {Object} payload The payload is an object containing the packages
 *     e.g.`{ packages: [...] }`
 */

export function packagesFetched({
  ...packages
}: PackagesFetchedOptions): PackagesFetchSucceeded {
  return {
    type: PACKAGES_FETCH_SUCCEEDED,
    payload: { ...packages },
  };
}
